import { useCallback, useMemo } from "react";

import type { DateValueType } from "../../DatepickerV2";
import type { DropdownOption } from "../../DropDownV2";
import type {
  LeadsPageAnalytics,
  ReturnedTranslatedContentLeadPageHook,
} from "../types";
import type { LeadTableFilterSubProps } from "./types";

import { useIsMobile } from "../../hooks";
import { formatToDateString } from "../../utils/date";
import { useDebounceFn } from "../../utils/debounce";
import { PII_FIELDS } from "../consts";
import { useLeadsContext } from "../providers/LeadsProvider";
import { DesktopFilters } from "./DesktopFilters";
import { MobileFilters } from "./MobileFilters";

type LeadFiltersProps = {
  analytics?: LeadsPageAnalytics;
  translations: ReturnedTranslatedContentLeadPageHook;
  isBackoffice?: boolean;
};

export const LeadFilters = ({
  analytics,
  translations,
  isBackoffice,
}: LeadFiltersProps) => {
  const {
    state: { searchQuery, dateRange },
    resources: {
      table: { data: tableData },
    },
    actions,
    runResource,
  } = useLeadsContext();
  const isMobile = useIsMobile();

  const canViewPII = !PII_FIELDS.every((field) =>
    tableData?.redactedFields?.includes(field),
  );

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchEvent(value);
  }, 1000);

  const handleDateRangePicker = useCallback(
    (value: DateValueType) => {
      if (
        formatToDateString(value?.startDate) ===
          formatToDateString(dateRange.from) ||
        formatToDateString(value?.endDate) === formatToDateString(dateRange.to)
      ) {
        analytics?.trackDateRangeChangeEvent(
          formatToDateString(value?.startDate),
          formatToDateString(value?.endDate),
        );
      }
      actions.setDateRange({
        from: value?.startDate ?? null,
        to: value?.endDate ?? null,
      });
    },
    [actions, analytics, dateRange.from, dateRange.to],
  );

  const handleLeadStatusDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newSelectedLeadStatuses = options.map((o) => o.value);
      actions.setSelected("leadStatuses", newSelectedLeadStatuses);
    },
    [actions],
  );

  const handleExportButton = useCallback(() => {
    runResource("csv");
    analytics?.trackTableExportCSVEvent({
      searchQuery,
      fromDate: formatToDateString(dateRange.from),
      toDate: formatToDateString(dateRange.to),
    });
  }, [runResource, dateRange.from, dateRange.to, searchQuery, analytics]);

  const filterConfig = useMemo<LeadTableFilterSubProps>(
    () => ({
      handleDateRangePicker,
      handleExportButton,
      handleLeadStatusDropdown,
      handleSearchInput,
      canViewPII,
      redactedFields: tableData?.redactedFields || [],
      translations,
      isBackoffice,
    }),
    [
      handleDateRangePicker,
      handleExportButton,
      handleLeadStatusDropdown,
      handleSearchInput,
      canViewPII,
      tableData?.redactedFields,
      translations,
      isBackoffice,
    ],
  );

  return isMobile ? (
    <MobileFilters {...filterConfig} />
  ) : (
    <DesktopFilters {...filterConfig} />
  );
};
